import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DocumentationSystem() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    // For now, redirect to assessment form since checkout requires user info
    // In production, this would open a checkout modal or redirect to Stripe
    navigate('/assessment/form?upsell=docs');
  };

  const templates = [
    'AI Acceptable Use Policy',
    'AI Tool Inventory',
    'AI Risk Register',
    'AI Incident Log',
    'Employee AI Agreement',
    'Vendor Assessment Template',
    'Accountability Checklist',
    'Governance Summary Report'
  ];

  const comparisonData = [
    { template: 'AI Acceptable Use Policy', score: '—', policy: '✓ Basic', inventory: '—', checklist: '—' },
    { template: 'AI Tool Inventory', score: '—', policy: '—', inventory: '✓ Basic', checklist: '—' },
    { template: 'AI Risk Register', score: '—', policy: '—', inventory: '—', checklist: '—' },
    { template: 'AI Incident Log', score: '—', policy: '—', inventory: '—', checklist: '—' },
    { template: 'Employee AI Agreement', score: '—', policy: '—', inventory: '—', checklist: '—' },
    { template: 'Vendor Assessment Template', score: '—', policy: '—', inventory: '—', checklist: '—' },
    { template: 'Accountability Checklist', score: '—', policy: '—', inventory: '—', checklist: '✓ Score only' },
    { template: 'Governance Summary Report', score: '—', policy: '—', inventory: '—', checklist: '—' },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Hero */}
          <div className="mb-12">
            <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
              $97 · ONE-TIME PAYMENT
            </p>
            <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
              Your organization is already using AI. Now document it properly.
            </h1>
            <p className="text-[18px] text-[#64748B]">
              Eight governance templates — ready to customize and use today.
            </p>
          </div>

          {/* Intro callout */}
          <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 mb-12">
            <p className="text-[15px] text-[#64748B] leading-relaxed">
              Already completed a free tool? Good — you have your starting point. The AI Accountability Documentation System is the complete system of 8 templates that turns that starting point into a defensible governance posture.
            </p>
          </div>

          {/* What's Included */}
          <div className="mb-12">
            <h2 className="text-[24px] font-bold text-[#102A43] mb-6">What's Included</h2>
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
              <ul className="space-y-3">
                {templates.map((template) => (
                  <li key={template} className="flex items-center gap-3">
                    <CheckCircle className="text-[#27AE60] flex-shrink-0" size={20} />
                    <span className="text-[15px] text-[#102A43]">{template}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mb-12">
            <h2 className="text-[24px] font-bold text-[#102A43] mb-6">Free Tools vs. Documentation System</h2>
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-[#102A43]">Template</th>
                      <th className="text-center py-3 px-4 font-semibold text-[#102A43]">Score tool</th>
                      <th className="text-center py-3 px-4 font-semibold text-[#102A43]">Policy tool</th>
                      <th className="text-center py-3 px-4 font-semibold text-[#102A43]">Inventory tool</th>
                      <th className="text-center py-3 px-4 font-semibold text-[#102A43]">Checklist tool</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, idx) => (
                      <tr key={row.template} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                        <td className="py-3 px-4 text-[#102A43]">{row.template}</td>
                        <td className="py-3 px-4 text-center text-[#64748B]">{row.score}</td>
                        <td className="py-3 px-4 text-center text-[#64748B]">{row.policy}</td>
                        <td className="py-3 px-4 text-center text-[#64748B]">{row.inventory}</td>
                        <td className="py-3 px-4 text-center text-[#64748B]">{row.checklist}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Positioning Note */}
          <div className="mb-12">
            <p className="text-[13px] text-[#94A3B8] leading-relaxed">
              These templates are aligned with AI governance expectations from the EU AI Act, NIST AI Risk Management Framework, and ISO/IEC 42001. They are operational tools, not legal documents. For legal advice, consult a qualified attorney.
            </p>
          </div>

          {/* Price & CTA */}
          <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 text-center">
            <p className="text-[28px] font-bold text-[#102A43] mb-2">
              $97
            </p>
            <p className="text-[15px] text-[#64748B] mb-6">
              One-time payment · Instant delivery
            </p>
            
            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="bg-[#2F80ED] text-white font-semibold px-8 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Get the Documentation System'}
            </button>
          </div>

          {/* Post-purchase upsell note */}
          <div className="mt-8 text-center">
            <p className="text-[14px] text-[#64748B]">
              After purchase: Ready to go deeper?{' '}
              <Link to="/assessment-product" className="text-[#2F80ED] hover:underline">
                Get a full AI Accountability Assessment — $297
              </Link>
            </p>
          </div>
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default DocumentationSystem;
